import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tryGenerate, tryLead, getTryCookieId } from '@/lib/api';
import { track } from '@/lib/analytics';
import { Logo } from '@/components/feature/Logo';
import { MeshBg } from '@/components/feature/MeshBg';

type Phase = 'idle' | 'loading' | 'result' | 'gate' | 'error';

const LOADING_MSGS = [
  'Finding your product…',
  'Setting the scene…',
  'Styling three shots…',
  'Adding the finishing polish…',
];

const LEAD_EMAIL_KEY = 'sp_try_lead_email';

function downloadPng(b64: string, filename: string) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: 'image/png' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function TryPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('idle');
  const [url, setUrl] = useState('');
  const [productName, setProductName] = useState('');
  const [frames, setFrames] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [msgIdx, setMsgIdx] = useState(0);

  // Email download gate
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingFrame, setPendingFrame] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [leadBusy, setLeadBusy] = useState(false);
  const knownEmail = useRef<string>('');

  useEffect(() => {
    track('try_page_view');
    try {
      knownEmail.current = localStorage.getItem(LEAD_EMAIL_KEY) || '';
    } catch {
      knownEmail.current = '';
    }
  }, []);

  // Rotate the loading copy while a generation is in flight.
  useEffect(() => {
    if (phase !== 'loading') return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length), 6000);
    return () => clearInterval(t);
  }, [phase]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setError('');
    setMsgIdx(0);
    setPhase('loading');
    track('try_generate_submitted');
    try {
      const res = await tryGenerate(url.trim(), getTryCookieId());
      if (!res.frames.length) {
        setError('We couldn’t generate that one. Try a different product URL.');
        setPhase('error');
        return;
      }
      setProductName(res.product.name || 'Your product');
      setFrames(res.frames);
      setPhase('result');
      track('try_generate_succeeded', { frames: res.frames.length });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong.';
      // 429 (guard: cookie/IP/global cap) is mapped to TransientUpstreamError by
      // lib/api.mapFailure — route those to the signup gate; everything else is
      // a plain error (couldn't find a product, render failed, etc.).
      const isGate = err instanceof Error && err.name === 'TransientUpstreamError';
      if (isGate) {
        setError(message);
        setPhase('gate');
        track('try_gate_hit');
      } else {
        setError(message);
        setPhase('error');
        track('try_generate_failed');
      }
    }
  };

  const requestDownload = (idx: number) => {
    if (knownEmail.current) {
      downloadPng(frames[idx], `${productName || 'ad'}-${idx + 1}.png`);
      track('try_download', { via: 'known_email' });
      return;
    }
    setPendingFrame(idx);
    setEmail('');
    setGateOpen(true);
  };

  const submitEmailAndDownload = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !value.includes('@')) return;
    setLeadBusy(true);
    try {
      await tryLead(value, url.trim());
    } catch {
      // Lead capture must never block the download the user was promised.
    }
    setLeadBusy(false);
    try {
      localStorage.setItem(LEAD_EMAIL_KEY, value);
    } catch {
      /* ignore */
    }
    knownEmail.current = value;
    track('try_download_email');
    if (pendingFrame !== null) {
      downloadPng(frames[pendingFrame], `${productName || 'ad'}-${pendingFrame + 1}.png`);
    }
    setGateOpen(false);
    setPendingFrame(null);
  };

  const startTrial = () => {
    track('try_start_trial_clicked', { from: phase });
    navigate('/checkout?plan=pro');
  };

  const lookAround = () => {
    track('try_look_around_clicked');
    navigate('/');
  };

  const resetToIdle = () => {
    setPhase('idle');
    setError('');
    setFrames([]);
    setProductName('');
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-surface flex flex-col items-center px-4 py-8">
      <MeshBg />

      <a href="/" className="relative z-10 inline-flex items-center mb-8">
        <Logo height={44} />
      </a>

      <div className="relative z-10 w-full max-w-3xl">
        {phase === 'idle' && (
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold text-dark leading-[1.15] tracking-tight [text-wrap:balance]">
              See your product as a{' '}
              <span className="text-primary">scroll-stopping ad</span>{' '}
              in 30 seconds.
            </h1>
            <p className="mt-4 text-dark-muted text-base md:text-lg max-w-xl mx-auto">
              Paste your store link. We grab your first product and generate three
              ready-to-post shots. Free, no signup.
            </p>
            <form onSubmit={handleGenerate} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
              <input
                type="text"
                inputMode="url"
                value={url}
                onChange={(ev) => setUrl(ev.target.value)}
                placeholder="yourstore.com"
                autoFocus
                className="flex-1 px-5 py-4 rounded-full border border-dark-muted/40 bg-white text-dark text-base placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
              <button
                type="submit"
                className="px-7 py-4 rounded-full bg-primary hover:bg-primary-hover text-white text-base font-bold whitespace-nowrap transition-all cursor-pointer shadow-lg shadow-primary/25"
              >
                Generate my Free Ads
              </button>
            </form>
            <p className="mt-3 text-xs text-dark-muted/70">Works best with Shopify stores or a product page URL.</p>
            <div className="mt-7 flex flex-col items-center gap-2.5">
              <span className="text-base text-dark-muted/80">Not ready yet?</span>
              <button
                type="button"
                onClick={lookAround}
                className="group inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white border border-dark-muted/25 text-dark font-semibold text-base shadow-sm hover:shadow-md hover:border-primary/50 hover:text-primary transition-all cursor-pointer"
              >
                <i className="ri-compass-3-line text-lg" />
                Let me look around
                <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        )}

        {phase === 'loading' && (
          <div className="text-center py-16">
            <div className="mx-auto w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
            <p className="mt-6 text-lg font-semibold text-dark">{LOADING_MSGS[msgIdx]}</p>
            <p className="mt-2 text-sm text-dark-muted">This takes about 30 seconds. Hang tight.</p>
          </div>
        )}

        {phase === 'result' && (
          <div className="pb-28 md:pb-0">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-dark">
                Here&rsquo;s <span className="text-primary">{productName}</span> 👇
              </h2>
              <p className="mt-2 text-dark-muted text-sm">Three fresh shots, made from your product photo.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {frames.map((f, i) => (
                <div key={i} className="rounded-2xl overflow-hidden bg-white gradient-stroke shadow-lg shadow-primary/10">
                  <img src={`data:image/png;base64,${f}`} alt={`${productName} ad ${i + 1}`} className="w-full aspect-square object-cover" />
                  <button
                    onClick={() => requestDownload(i)}
                    className="w-full flex items-center justify-center gap-1.5 px-4 py-3 border-t border-surface-dark/20 text-primary text-sm font-semibold hover:bg-primary/5 active:bg-primary/10 transition-colors cursor-pointer"
                  >
                    <i className="ri-download-line" /> Download
                  </button>
                </div>
              ))}
            </div>
            <div className="hidden md:block mt-8 text-center gradient-stroke bg-white rounded-2xl p-6 shadow-xl shadow-primary/15">
              <p className="text-dark font-bold text-lg">Want more styles, more products, and a full campaign?</p>
              <p className="text-dark-muted text-sm mt-1">Start your free trial. 90+ scenes, carousels, and one-click posting.</p>
              <button
                onClick={startTrial}
                className="mt-4 px-8 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer shadow-lg shadow-primary/25"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        )}

        {phase === 'gate' && (
          <div className="text-center gradient-stroke bg-white rounded-2xl p-8 shadow-xl shadow-primary/15 max-w-lg mx-auto">
            <i className="ri-sparkling-2-line text-3xl text-primary" />
            <h2 className="mt-3 text-2xl font-bold text-dark">You&rsquo;ve used your free sample</h2>
            <p className="mt-2 text-dark-muted text-sm">{error || 'Sign up to generate more.'}</p>
            <button
              onClick={startTrial}
              className="mt-5 px-8 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer shadow-lg shadow-primary/25"
            >
              Start Free Trial
            </button>
          </div>
        )}

        {phase === 'error' && (
          <div className="text-center gradient-stroke bg-white rounded-2xl p-8 shadow-xl shadow-primary/15 max-w-lg mx-auto">
            <i className="ri-error-warning-line text-3xl text-primary" />
            <h2 className="mt-3 text-2xl font-bold text-dark">Hmm, that didn&rsquo;t work</h2>
            <p className="mt-2 text-dark-muted text-sm">{error}</p>
            <button
              onClick={resetToIdle}
              className="mt-5 px-8 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
            >
              Try another URL
            </button>
          </div>
        )}
      </div>

      {phase === 'result' && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] bg-surface/95 backdrop-blur border-t border-surface-dark/30 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
          <button
            onClick={startTrial}
            className="w-full px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-lg shadow-primary/25 cursor-pointer"
          >
            Start Free Trial · more styles &amp; posting
          </button>
        </div>
      )}

      {gateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark/40 backdrop-blur-sm">
          <div className="w-full max-w-sm gradient-stroke bg-white rounded-2xl p-7 shadow-2xl">
            <h3 className="text-lg font-bold text-dark">Where should we send it?</h3>
            <p className="text-dark-muted text-sm mt-1">Enter your email to download this shot.</p>
            <form onSubmit={submitEmailAndDownload} className="mt-4 space-y-3">
              <input
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                className="w-full px-4 py-3 rounded-xl border border-dark-muted/40 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
              <button
                type="submit"
                disabled={leadBusy}
                className="w-full px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60"
              >
                {leadBusy ? 'One sec…' : 'Download'}
              </button>
              <button
                type="button"
                onClick={() => { setGateOpen(false); setPendingFrame(null); }}
                className="w-full text-center text-xs text-dark-muted hover:text-dark transition-colors"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
